import React, { useState } from "react";
import { ArrowUpRight, Mail, Phone, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  surface: "",
  budget: "",
  message: "",
};

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialState);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.project_type) {
      toast.error(t("contact.form.error"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, locale: lang });
      toast.success(t("contact.form.success"));
      setForm(initialState);
    } catch (err) {
      toast.error(t("contact.form.error"));
    } finally {
      setSending(false);
    }
  };

  const projectOptions = t("contact.form.project_options") || [];
  const budgetOptions = t("contact.form.budget_options") || [];

  return (
    <section id="contacto" data-testid="contact-section" className="section-pad bg-[#111] text-white">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Title col */}
          <div className="lg:col-span-5">
            <div className="font-mono-label text-[#A89F91] mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#A89F91]" />
              {t("contact.kicker")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl">
              <span className="block">{t("contact.title_a")}</span>
              <span className="block text-[#A89F91] italic font-light" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 400 }}>
                {t("contact.title_b")}
              </span>
            </h2>
            <p className="mt-8 text-[#A3A3A3] leading-relaxed max-w-md">
              {t("contact.subtitle")}
            </p>

            <div className="mt-14 space-y-8">
              <div>
                <div className="font-mono-label text-[#A89F91] mb-3">{t("contact.info.title")}</div>
                <div className="flex items-start gap-3 text-white">
                  <MapPin size={18} strokeWidth={1.25} className="mt-1 text-[#A89F91]" />
                  <div className="whitespace-pre-line">{t("contact.info.address")}</div>
                </div>
              </div>
              <a
                href={`mailto:${t("contact.info.email")}`}
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-white hover:text-[#A89F91] transition-colors"
              >
                <Mail size={18} strokeWidth={1.25} className="text-[#A89F91]" />
                {t("contact.info.email")}
              </a>
              <a
                href={`tel:${(t("contact.info.phone") || "").replace(/\s/g, "")}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-white hover:text-[#A89F91] transition-colors"
              >
                <Phone size={18} strokeWidth={1.25} className="text-[#A89F91]" />
                {t("contact.info.phone")}
              </a>
              <div className="flex items-center gap-3 text-[#A3A3A3]">
                <Clock size={18} strokeWidth={1.25} className="text-[#A89F91]" />
                {t("contact.info.hours")}
              </div>
            </div>
          </div>

          {/* Form col */}
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-[#262626]"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <div className="md:col-span-2">
                <label className="font-mono-label text-[#A89F91]">{t("contact.form.name")}</label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  className="input-line input-line-dark"
                  required
                />
              </div>
              <div>
                <label className="font-mono-label text-[#A89F91]">{t("contact.form.email")}</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="input-line input-line-dark"
                  required
                />
              </div>
              <div>
                <label className="font-mono-label text-[#A89F91]">{t("contact.form.phone")}</label>
                <input
                  data-testid="contact-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="input-line input-line-dark"
                  required
                />
              </div>
              <div>
                <label className="font-mono-label text-[#A89F91]">{t("contact.form.project_type")}</label>
                <select
                  data-testid="contact-project-type-select"
                  value={form.project_type}
                  onChange={update("project_type")}
                  className="input-line input-line-dark"
                  required
                  style={{ appearance: "none" }}
                >
                  <option value="" className="bg-[#111] text-white">{t("contact.form.select_default")}</option>
                  {projectOptions.map((o, i) => (
                    <option key={i} value={o} className="bg-[#111] text-white">
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono-label text-[#A89F91]">{t("contact.form.surface")}</label>
                <input
                  data-testid="contact-surface-input"
                  type="text"
                  value={form.surface}
                  onChange={update("surface")}
                  className="input-line input-line-dark"
                  placeholder="120"
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-label text-[#A89F91]">{t("contact.form.budget")}</label>
                <select
                  data-testid="contact-budget-select"
                  value={form.budget}
                  onChange={update("budget")}
                  className="input-line input-line-dark"
                  style={{ appearance: "none" }}
                >
                  <option value="" className="bg-[#111] text-white">{t("contact.form.select_default")}</option>
                  {budgetOptions.map((o, i) => (
                    <option key={i} value={o} className="bg-[#111] text-white">
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-label text-[#A89F91]">{t("contact.form.message")}</label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  className="input-line input-line-dark resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6">
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="btn-on-dark disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? t("contact.form.sending") : t("contact.form.submit")}
                {!sending && <ArrowUpRight size={16} strokeWidth={1.5} />}
              </button>
              <span className="font-mono-label text-[#A3A3A3]">— 48h</span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
